"use client"

import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"
import { useTrackEvent } from "~/hooks/use-track-event"

/** Form/auth flows - scroll depth there is not a content-engagement signal. */
const EXCLUDED_PREFIXES = ["/auth", "/submit"]

/**
 * Maps a pathname to the `location` property of `marketing.page_scrolled`.
 * "/" stays "home" for continuity with pre-existing data; other pages use the
 * pathname without the leading slash (e.g. "blog", "blog/my-post", "tools/jina").
 * Returns null for excluded routes.
 */
export const deriveScrollLocation = (pathname: string): string | null => {
  if (EXCLUDED_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return null
  }
  if (pathname === "/") return "home"
  // Split/join instead of a trim regex: `\/+$` backtracks super-linearly (Sonar S5852).
  return pathname.split("/").filter(Boolean).join("/")
}

/**
 * Tracks max scroll depth on the current page and emits a `marketing.page_scrolled`
 * event when the user navigates away or closes the tab. Mounted once in the
 * `(web)` layout so every marketing/content page reports without per-page wiring.
 * Carries `max_percentage`, `max_pixels`, and `scrolled_25/50/75` thresholds so
 * the content-optimization insights can compute engaged-read rates per `location`
 * (see `packages/marketing/docs/09-event-taxonomy.md`).
 */
export const ScrollTracker = () => {
  const trackEvent = useTrackEvent()
  const maxPercentage = useRef(0)
  const maxPixels = useRef(0)
  const pathname = usePathname()

  useEffect(() => {
    const location = deriveScrollLocation(pathname)
    if (!location) return

    // Reset on route change so each page gets its own measurement.
    maxPercentage.current = 0
    maxPixels.current = 0

    const measure = () => {
      const scrolled = window.scrollY + window.innerHeight
      const total = document.documentElement.scrollHeight || 1
      const pct = Math.min(1, scrolled / total)
      if (pct > maxPercentage.current) maxPercentage.current = pct
      if (scrolled > maxPixels.current) maxPixels.current = scrolled
    }

    // Capture initial position (page may load already scrolled, e.g. anchor links).
    measure()

    window.addEventListener("scroll", measure, { passive: true })

    const flush = () => {
      if (maxPixels.current === 0) return
      trackEvent(
        "marketing.page_scrolled",
        {
          location,
          max_percentage: Number(maxPercentage.current.toFixed(2)),
          max_pixels: Math.round(maxPixels.current),
          scrolled_25: maxPercentage.current >= 0.25,
          scrolled_50: maxPercentage.current >= 0.5,
          scrolled_75: maxPercentage.current >= 0.75,
        },
        { plausibleEventName: "page_scrolled" },
      )
      // beforeunload + effect cleanup can both fire during a tab close. The
      // first call sends the event, the second early-returns on the guard
      // above. Without this reset PostHog would receive a duplicate event
      // for the same pageview.
      maxPixels.current = 0
      maxPercentage.current = 0
    }

    // Tab close / hard navigation.
    window.addEventListener("beforeunload", flush)

    return () => {
      window.removeEventListener("scroll", measure)
      window.removeEventListener("beforeunload", flush)
      // SPA navigation: pathname changed, fire before the new page mounts its tracker.
      flush()
    }
  }, [pathname, trackEvent])

  return null
}
